// TEN8-F1-S3: approved desktop permit editor shared by the two authorized surfaces.
import type { LocationPermitDto, LocationPermitInput, LocationPermitListDto } from "./api.ts";
interface Reply<T> {
  status: number;
  body: (T & { error?: string; message?: string }) | null;
}
export interface LocationPermitHost {
  request<T>(method: "GET" | "POST", path: string, body?: LocationPermitInput): Promise<Reply<T>>;
  basePath: string;
  locationId: string;
  active(): boolean;
  canManage(): boolean;
}
export interface LocationPermitView {
  load(): Promise<void>;
  clear(): void;
  clearSensitive(): void;
  dirty(): boolean;
  busy(): boolean;
  confirmLeave(): boolean;
  refreshAccess(): void;
}
function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  return element;
}
function action(text: string): HTMLButtonElement {
  const element = node("button", text);
  element.type = "button";
  element.className = "btn-secondary";
  return element;
}
function formInput(maxLength: number): HTMLInputElement {
  const element = node("input");
  element.type = "text";
  element.className = "field-input";
  element.maxLength = maxLength;
  element.required = true;
  return element;
}
export function createLocationPermits(
  parent: HTMLElement,
  host: LocationPermitHost,
): LocationPermitView {
  const section = node("section");
  section.className = "location-permits";
  const header = node("div");
  header.className = "location-permits-heading";
  header.append(node("h3", "ABC permits"));
  const add = action("Add permit");
  header.append(add);
  const status = node("p");
  status.setAttribute("role", "status");
  const retry = action("Retry");
  retry.hidden = true;
  const records = node("div");
  records.className = "location-permits-records";
  const form = node("form");
  form.className = "location-permits-editor";
  form.hidden = true;
  const formTitle = node("h4");
  const grid = node("div");
  grid.className = "location-permits-grid";
  const issuing = formInput(200),
    permitType = formInput(200),
    holderName = formInput(200),
    number = formInput(256),
    expiresOnInput = node("input");
  // AUTOFILL1: no whole-form autocomplete off. The permit number is an identifier and stays unfilled; the issuing
  // authority and permit type are not personal data, so a manager has nothing useful to offer for them.
  issuing.autocomplete = "off";
  permitType.autocomplete = "off";
  number.type = "password";
  number.autocomplete = "off";
  number.spellcheck = false;
  number.setAttribute("autocapitalize", "none");
  number.hidden = true;
  expiresOnInput.type = "date";
  expiresOnInput.className = "field-input";
  const entity = node("select");
  entity.className = "field-input";
  entity.required = true;
  const recordedStatus = node("select");
  recordedStatus.className = "field-input";
  recordedStatus.required = true;
  for (const [value, label] of [
    ["", "Select status"],
    ["recorded", "Recorded"],
    ["inactive", "Inactive"],
    ["unknown", "Unknown"],
  ]) {
    const option = node("option", label);
    option.value = value!;
    recordedStatus.append(option);
  }
  const mask = node("span");
  mask.className = "location-permits-mask";
  const replace = action("Replace number");
  const numberRow = node("div");
  numberRow.className = "location-permits-number";
  numberRow.append(mask, replace, number);
  function field(label: string, control: HTMLElement): HTMLElement {
    const item = node("div");
    item.className = "location-permits-field";
    const caption = node("label", label);
    const target = control === numberRow ? number : control;
    target.id = `permit-field-${crypto.randomUUID()}`;
    caption.htmlFor = target.id;
    item.append(caption, control);
    grid.append(item);
    return item;
  }
  field("Issuing authority", issuing);
  field("Permit type", permitType);
  field("Permit number", numberRow);
  field("Status", recordedStatus);
  field("Holder entity", entity);
  const holderField = field("Holder name", holderName);
  holderField.classList.add("location-permits-wide");
  field("Expires", expiresOnInput);
  const error = node("p");
  error.className = "admin-error";
  error.setAttribute("role", "alert");
  const footer = node("div");
  footer.className = "location-permits-actions";
  const reload = action("Reload"),
    review = action("Use these changes"),
    cancel = action("Cancel"),
    save = action("Save permit");
  save.type = "submit";
  save.className = "btn-primary";
  reload.hidden = true;
  review.hidden = true;
  footer.append(error, reload, review, cancel, save);
  form.append(formTitle, grid, footer);
  section.append(header, status, retry, records, form);
  parent.append(section);
  let epoch = 0,
    requestKey: string | undefined,
    available = false,
    saving = false,
    loading = false,
    writeAllowed = false;
  let permits: LocationPermitDto[] = [],
    entities: Array<{ id: string; name: string }> = [],
    editing: LocationPermitDto | undefined;
  let baseline = "",
    replacement = false,
    conflicted = false,
    reviewVersion: number | undefined;
  const current = (captured: number) => captured === epoch && host.active() && section.isConnected;
  const metadata = () => ({
    issuingAuthority: issuing.value.trim(),
    permitType: permitType.value.trim(),
    holderEntityId: entity.value,
    holderName: holderName.value.trim(),
    expiresOn: expiresOnInput.value || null,
    status: recordedStatus.value as LocationPermitInput["status"],
  });
  const dirty = () =>
    !form.hidden && (JSON.stringify(metadata()) !== baseline || number.value !== "" || replacement);
  function permissions(): boolean {
    return available && writeAllowed && host.canManage();
  }
  function clearSensitive(): void {
    number.value = "";
    for (const input of [issuing, permitType, holderName, expiresOnInput]) input.value = "";
    entity.value = "";
    recordedStatus.value = "";
    mask.textContent = "";
    number.hidden = true;
    number.required = false;
    replacement = false;
    requestKey = undefined;
    form.hidden = true;
    editing = undefined;
    baseline = "";
    conflicted = false;
    reviewVersion = undefined;
    error.textContent = "";
    reload.hidden = true;
    review.hidden = true;
  }
  function clear(): void {
    epoch++;
    section.hidden = true;
    section.remove();
    clearSensitive();
    saving = false;
    loading = false;
    available = false;
    writeAllowed = false;
    permits = [];
    entities = [];
    records.replaceChildren();
    status.textContent = "";
    retry.hidden = true;
    add.hidden = true;
  }
  function fillEntities(): void {
    const selected = entity.value;
    entity.replaceChildren();
    const blank = node("option", "Select entity");
    blank.value = "";
    entity.append(blank);
    for (const choice of entities) {
      const option = node("option", choice.name);
      option.value = choice.id;
      entity.append(option);
    }
    if (entities.some((choice) => choice.id === selected)) entity.value = selected;
  }
  function sync(): void {
    save.textContent = saving ? "Saving" : "Save permit";
    add.hidden = !permissions();
    add.disabled = saving || loading;
    for (const control of form.querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >("input,select,button"))
      control.disabled = saving || loading || !permissions();
    save.disabled = saving || loading || !permissions() || conflicted;
    cancel.disabled = saving || loading;
    reload.disabled = saving || loading;
    review.disabled = saving || loading;
  }
  function renderRecords(): void {
    records.replaceChildren();
    if (!available) return;
    if (!permits.length) {
      records.append(node("p", "No permits"));
      return;
    }
    const table = node("table");
    table.className = "location-permits-table";
    const head = node("thead"),
      headRow = node("tr");
    for (const label of ["Permit", "Number", "Holder", "Expires", "Status", ""])
      headRow.append(node("th", label));
    head.append(headRow);
    table.append(head);
    const body = node("tbody");
    for (const permit of permits) {
      const row = node("tr");
      const title = node("td", permit.permitType),
        authority = node("span", permit.issuingAuthority);
      authority.className = "location-permits-authority";
      title.append(authority);
      row.append(
        title,
        node("td", permit.number.masked),
        node("td", permit.holderName),
        node("td", permit.expiresOn ?? "Not recorded"),
        node(
          "td",
          { recorded: "Recorded", inactive: "Inactive", unknown: "Unknown" }[permit.status],
        ),
      );
      const controls = node("td");
      if (permissions()) {
        const edit = action("Edit");
        edit.disabled = saving || loading;
        edit.onclick = () => open(permit);
        controls.append(edit);
      }
      row.append(controls);
      body.append(row);
    }
    table.append(body);
    records.append(table);
  }
  function open(permit?: LocationPermitDto): void {
    if (!permissions() || saving || loading) return;
    if (dirty() && !window.confirm("Discard the unsaved permit changes?")) return;
    clearSensitive();
    editing = permit;
    issuing.value = permit?.issuingAuthority ?? "";
    permitType.value = permit?.permitType ?? "";
    holderName.value = permit?.holderName ?? "";
    expiresOnInput.value = permit?.expiresOn ?? "";
    recordedStatus.value = permit?.status ?? "";
    fillEntities();
    entity.value = permit?.holderEntityId ?? "";
    mask.textContent = permit?.number.masked ?? "";
    replacement = permit === undefined;
    mask.hidden = replacement;
    replace.hidden = replacement;
    number.hidden = !replacement;
    number.required = replacement;
    formTitle.textContent = permit ? "Edit permit" : "Add permit";
    baseline = JSON.stringify(metadata());
    form.hidden = false;
    sync();
    issuing.focus();
  }
  function refuse(): void {
    clearSensitive();
    available = false;
    writeAllowed = false;
    permits = [];
    entities = [];
    records.replaceChildren();
    status.textContent = "Permits are no longer available.";
    retry.hidden = false;
    sync();
  }
  async function read(retainDraft = false): Promise<void> {
    if (!host.active()) return;
    if (!section.isConnected) parent.append(section);
    section.hidden = false;
    const captured = ++epoch;
    loading = true;
    if (!retainDraft) clearSensitive();
    available = false;
    records.replaceChildren();
    status.textContent = "Loading permits.";
    retry.hidden = true;
    sync();
    try {
      const response = await host.request<LocationPermitListDto>("GET", host.basePath);
      if (!current(captured)) return;
      if (response.status === 401 || response.status === 403) {
        refuse();
        return;
      }
      if (
        response.status !== 200 ||
        !Array.isArray(response.body?.permits) ||
        !response.body.permits.every((permit) => permit.locationId === host.locationId) ||
        !Array.isArray(response.body.holderEntities) ||
        typeof response.body.canManage !== "boolean"
      ) {
        status.textContent = "Could not load permits.";
        retry.hidden = false;
        return;
      }
      permits = response.body.permits;
      entities = response.body.holderEntities;
      writeAllowed = response.body.canManage;
      available = true;
      status.textContent = "";
      if (!permissions()) clearSensitive();
      if (retainDraft && !form.hidden) {
        fillEntities();
        const latest = editing && permits.find((permit) => permit.id === editing?.id);
        if (editing && !latest) {
          error.textContent = "This permit is no longer available.";
          reviewVersion = undefined;
        } else {
          reviewVersion = latest?.version ?? 0;
          mask.textContent = latest?.number.masked ?? "";
          review.hidden = false;
          reload.hidden = true;
          error.textContent = "Current version loaded.";
        }
      }
      renderRecords();
    } catch {
      if (current(captured)) {
        status.textContent = "Could not load permits.";
        retry.hidden = false;
      }
    } finally {
      if (current(captured)) {
        loading = false;
        sync();
      }
    }
  }
  add.onclick = () => open();
  cancel.onclick = () => {
    if (!saving) clearSensitive();
  };
  replace.onclick = () => {
    if (!permissions() || saving) return;
    number.value = "";
    replacement = true;
    number.hidden = false;
    number.required = true;
    mask.hidden = true;
    replace.hidden = true;
    requestKey = undefined;
    number.focus();
  };
  retry.onclick = () => void read(!form.hidden && conflicted);
  reload.onclick = () => {
    if (saving) return;
    number.value = "";
    requestKey = undefined;
    void read(true);
  };
  review.onclick = () => {
    if (reviewVersion === undefined || !permissions()) return;
    if (editing) editing = { ...editing, version: reviewVersion };
    reviewVersion = undefined;
    conflicted = false;
    requestKey = undefined;
    review.hidden = true;
    error.textContent = "";
    sync();
  };
  const edited = () => {
    if (!saving) {
      requestKey = undefined;
      review.hidden = reviewVersion === undefined;
      if (reviewVersion !== undefined) conflicted = true;
    }
  };
  form.addEventListener("input", edited);
  form.addEventListener("change", edited);
  form.onsubmit = (event) => {
    event.preventDefault();
    if (
      saving ||
      loading ||
      conflicted ||
      !permissions() ||
      !host.active() ||
      !form.reportValidity()
    )
      return;
    requestKey ??= crypto.randomUUID();
    const captured = epoch;
    const permitId = editing?.id;
    const expectedVersion = editing?.version ?? 0;
    saving = true;
    error.textContent = "";
    sync();
    renderRecords();
    void (async () => {
      try {
        const response = await host.request<{ permit?: LocationPermitDto; replayed?: boolean }>(
          "POST",
          `${host.basePath}${permitId === undefined ? "" : `/${encodeURIComponent(permitId)}`}`,
          {
            ...metadata(),
            expectedVersion,
            idempotencyKey: requestKey!,
            number: replacement ? { kind: "replace", value: number.value } : { kind: "keep" },
          },
        );
        if (!current(captured)) {
          number.value = "";
          return;
        }
        if (response.status === 401 || response.status === 403) {
          refuse();
          return;
        }
        if (response.status === 200 || response.status === 201) {
          number.value = "";
          clearSensitive();
          saving = false;
          await read();
          if (host.active() && available) status.textContent = "Saved";
          return;
        }
        if (response.status === 409) {
          conflicted = true;
          reload.hidden = false;
          review.hidden = true;
          error.textContent = "This permit changed. Reload before saving.";
          return;
        }
        error.textContent = "Could not save the permit. Retry.";
      } catch {
        if (current(captured)) error.textContent = "Could not save the permit. Retry.";
      } finally {
        if (current(captured)) {
          saving = false;
          sync();
          renderRecords();
        }
      }
    })();
  };
  clear();
  return {
    load: () => read(),
    clear,
    clearSensitive,
    dirty,
    busy: () => saving,
    refreshAccess: () => {
      if (!host.active()) {
        clear();
        return;
      }
      if (!host.canManage()) {
        clearSensitive();
        renderRecords();
        sync();
      }
    },
    confirmLeave: () =>
      !saving && (!dirty() || window.confirm("Discard the unsaved permit changes?")),
  };
}
